import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Phone, MessageCircle, FileText, PhoneCall, Building, Upload,
         RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { AvisoFaltanDatos, DialogoDatosFacturacion } from '@/components/DatosFacturacion';
import { mensajeDeError } from '@/lib/errores';

// ===================================================================
// TODO LO QUE HAY AQUI ARRIBA VIVE A NIVEL DE MODULO, Y ES DELIBERADO
// ===================================================================
// Un componente declarado DENTRO de otro se vuelve a crear en cada render del
// padre. React lo ve como un tipo distinto, desmonta el anterior y monta uno
// nuevo: el input pierde el foco en cada tecla y escribir es imposible.
//
// Paso literalmente eso en Quotes.jsx la semana pasada. Aqui hay cuatro
// dialogos con campos de texto, asi que la trampa estaba servida.

const COLORES_BADGE = {
  blue: 'bg-blue-100 text-blue-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  purple: 'bg-purple-100 text-purple-800',
  orange: 'bg-orange-100 text-orange-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-800',
};

/**
 * Cuanto hace que nadie toca esta ficha, traducido a color.
 *
 * Es el corazon del tablero: sin esto no hay forma de distinguir a alguien que
 * lleva dos dias parado de alguien que lleva dos meses. Hoy son la misma
 * tarjeta, y por eso los leads se mueren sin que nadie se entere.
 *
 * Umbrales por columna, porque no todas corren igual: en Perdido no se
 * persigue a nadie y ponerlo en rojo seria ruido, asi que 0 desactiva el color.
 */
export function nivelDeAlerta(dias, warnDays, alertDays) {
  if (dias === null || dias === undefined) return 'ok';
  if (!alertDays && !warnDays) return 'ok';        // columna sin vigilancia
  if (alertDays && dias >= alertDays) return 'alerta';
  if (warnDays && dias >= warnDays) return 'aviso';
  return 'ok';
}

const ESTILO_ALERTA = {
  ok: { tarjeta: 'border-slate-200', texto: 'text-slate-500' },
  aviso: { tarjeta: 'border-amber-400 bg-amber-50', texto: 'text-amber-700 font-medium' },
  alerta: { tarjeta: 'border-red-400 bg-red-50', texto: 'text-red-700 font-semibold' },
};

export function textoDiasParado(dias) {
  if (dias === null || dias === undefined) return '';
  if (dias === 0) return 'hoy';
  if (dias === 1) return '1 día parado';
  return `${dias} días parados`;
}

export function textoIntentos(intentos) {
  if (!intentos) return 'sin llamar';
  return intentos === 1 ? '1er intento' : `${intentos}º intento`;
}

const eur = (n) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

const soloDigitos = (tel) => (tel || '').replace(/[^\d+]/g, '');

// ===================================================================
// TARJETA
// ===================================================================

export function TarjetaLead({ lead, columna, onLlamar, onPresupuesto, onAbrir, onDatos, ocupado }) {
  const nivel = nivelDeAlerta(lead.days_stalled, columna?.warn_days, columna?.alert_days);
  const estilo = ESTILO_ALERTA[nivel];
  const tel = soloDigitos(lead.phone);

  const parar = (e) => e.stopPropagation();

  return (
    <div
      onClick={() => onAbrir(lead.id)}
      className={`p-2.5 bg-white border rounded-md hover:border-blue-400 transition-all cursor-grab active:cursor-grabbing ${estilo.tarjeta}`}
      data-testid={`lead-card-${lead.id}`}
      data-nivel={nivel}
    >
      <div className="flex items-start justify-between gap-1">
        <h4 className="font-medium text-sm text-slate-900 truncate" data-testid="lead-name">
          {lead.name}
        </h4>
        {tel && (
          <div className="flex gap-1 shrink-0" onClick={parar}>
            <a href={`tel:${tel}`} title="Llamar" data-testid="lead-tel"
               className="text-slate-400 hover:text-slate-700">
              <Phone className="w-3.5 h-3.5" />
            </a>
            <a href={`https://wa.me/${tel.replace('+', '')}`} target="_blank" rel="noreferrer"
               title="WhatsApp" data-testid="lead-whatsapp"
               className="text-slate-400 hover:text-green-600">
              <MessageCircle className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>

      {lead.company && (
        <p className="text-xs text-slate-500 truncate mt-0.5">
          <Building className="w-3 h-3 inline mr-1" />
          {lead.company}
        </p>
      )}

      {/* La linea que da sentido al tablero: cuantas veces y desde cuando. */}
      <p className={`text-xs mt-1 ${estilo.texto}`} data-testid="lead-seguimiento">
        {textoIntentos(lead.contact_attempts)}
        {lead.days_stalled !== null && lead.days_stalled !== undefined && (
          <> · {textoDiasParado(lead.days_stalled)}</>
        )}
      </p>

      {lead.quote_total ? (
        <p className="text-xs font-semibold text-slate-700 text-right mt-1"
           data-testid="lead-importe">
          {eur(lead.quote_total)}
        </p>
      ) : null}

      {lead.lost_reason && (
        <p className="text-xs text-slate-500 italic mt-1" data-testid="lead-motivo-perdida">
          {lead.lost_reason}
        </p>
      )}

      {/* Ha pagado pero le faltan papeles. El comercial lo ve aqui para
          poder pedirselos, que es justo el momento en que tiene al cliente
          contento y al telefono. */}
      {lead.paid_at && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-100" onClick={parar}>
          <AvisoFaltanDatos estado={lead} onAbrir={() => onDatos?.(lead)} compacto />
        </div>
      )}

      <div className="flex gap-1 mt-2" onClick={parar}>
        <Button
          size="sm" variant="outline" disabled={ocupado}
          onClick={() => onLlamar(lead)}
          className="flex-1 h-6 text-xs"
          data-testid="btn-he-llamado"
        >
          <PhoneCall className="w-3 h-3 mr-1" />
          He llamado
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => onPresupuesto(lead)}
          className="h-6 text-xs px-2"
          title="Crear presupuesto"
          data-testid="btn-crear-presupuesto"
        >
          <FileText className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ===================================================================
// DIALOGOS
// ===================================================================

export function DialogoPerdido({ open, onOpenChange, lead, motivos, onConfirmar }) {
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (open) { setMotivo(''); setDetalle(''); }
  }, [open, lead?.id]);

  const confirmar = async () => {
    if (!motivo) return;
    setEnviando(true);
    try {
      await onConfirmar(lead, motivo, detalle);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialogo-perdido">
        <DialogHeader>
          <DialogTitle>Dar por perdido: {lead?.name}</DialogTitle>
          <DialogDescription>
            El motivo es obligatorio. Sin él no hay forma de saber por qué se
            pierden las ventas, que es justo lo que el embudo tiene que contarte.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Motivo</Label>
            <div className="grid gap-1.5">
              {motivos.map((m) => (
                <label key={m.key}
                       className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio" name="motivo-perdida" value={m.key}
                    checked={motivo === m.key}
                    onChange={(e) => setMotivo(e.target.value)}
                    data-testid={`motivo-${m.key}`}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="detalle-perdida">Detalle (opcional)</Label>
            <Textarea
              id="detalle-perdida" rows={2} value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Se fue con X porque le regalaban el TPV"
              data-testid="detalle-perdida"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={confirmar}
            disabled={!motivo || enviando}
            className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
            data-testid="confirmar-perdida"
          >
            Dar por perdido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DialogoCobro({ open, onOpenChange, lead, onConfirmar }) {
  const [importe, setImporte] = useState('');
  const [metodo, setMetodo] = useState('transferencia');
  const [fecha, setFecha] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (open) {
      setImporte(lead?.quote_total ? String(lead.quote_total) : '');
      setMetodo('transferencia');
      setFecha(new Date().toISOString().slice(0, 10));
    }
  }, [open, lead?.id, lead?.quote_total]);

  const confirmar = async () => {
    setEnviando(true);
    try {
      await onConfirmar(lead, {
        importe: importe ? Number(importe) : null,
        metodo,
        fecha_cobro: fecha ? new Date(`${fecha}T12:00:00Z`).toISOString() : null,
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialogo-cobro">
        <DialogHeader>
          <DialogTitle>Confirmar cobro: {lead?.name}</DialogTitle>
          <DialogDescription>
            A partir de aquí pasa a ser cliente. La ficha se queda en Ganado
            hasta que tengas sus datos fiscales y le emitas la factura.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="fecha-cobro">Fecha del cobro</Label>
            <Input
              id="fecha-cobro" type="date" value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              data-testid="fecha-cobro"
            />
            <p className="text-xs text-slate-500">
              La fecha en que entró el dinero, no la de hoy. Es la que irá en la
              factura como fecha de operación.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="importe-cobro">Importe</Label>
              <Input
                id="importe-cobro" type="number" step="0.01" value={importe}
                onChange={(e) => setImporte(e.target.value)}
                data-testid="importe-cobro"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="metodo-cobro">Método</Label>
              <select
                id="metodo-cobro" value={metodo}
                onChange={(e) => setMetodo(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                data-testid="metodo-cobro"
              >
                <option value="transferencia">Transferencia</option>
                <option value="recibo">Recibo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="efectivo">Efectivo</option>
              </select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={confirmar} disabled={enviando}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="confirmar-cobro"
          >
            Confirmar cobro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DialogoNuevoLead({ open, onOpenChange, onCreado }) {
  const [datos, setDatos] = useState({ name: '', email: '', phone: '', company: '', notes: '' });
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (open) setDatos({ name: '', email: '', phone: '', company: '', notes: '' });
  }, [open]);

  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await onCreado(datos);
    } finally {
      setEnviando(false);
    }
  };

  const campo = (k) => (e) => setDatos((d) => ({ ...d, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo lead</DialogTitle>
          <DialogDescription>
            Con el nombre y el teléfono basta. El resto se pide cuando pague.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={enviar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre</Label>
            <Input id="name" data-testid="lead-name-input"
                   value={datos.name} onChange={campo('name')} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input id="phone" data-testid="lead-phone-input"
                   value={datos.phone} onChange={campo('phone')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="lead-email-input"
                   value={datos.email} onChange={campo('email')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Negocio</Label>
            <Input id="company" data-testid="lead-company-input"
                   value={datos.company} onChange={campo('company')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea id="notes" rows={3} data-testid="lead-notes-input"
                      value={datos.notes} onChange={campo('notes')} />
          </div>
          <Button type="submit" disabled={enviando}
                  className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                  data-testid="lead-submit-btn">
            Crear lead
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===================================================================
// TABLERO
// ===================================================================

export default function CRM({ user }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [columnas, setColumnas] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [llamando, setLlamando] = useState(null);
  // La hoja de las campañas de Meta: se mira sola cada cuarto de hora.
  const [importando, setImportando] = useState(false);
  const ficheroMeta = useRef(null);
  const [dialogoMeta, setDialogoMeta] = useState(false);
  const [configMeta, setConfigMeta] = useState(null);
  const [urlMeta, setUrlMeta] = useState('');
  const [guardandoMeta, setGuardandoMeta] = useState(false);

  const [dialogoNuevo, setDialogoNuevo] = useState(false);
  const [fichaPerdida, setFichaPerdida] = useState(null);
  const [fichaCobro, setFichaCobro] = useState(null);
  const [fichaDatos, setFichaDatos] = useState(null);

  /**
   * Sube el CSV que exporta Meta Lead Ads y mete los leads en la primera
   * columna del tablero.
   *
   * Angel, 22-sep-2026: la agencia deja los leads en una hoja compartida.
   * Esto es el puente hasta que conecten el webhook.
   *
   * Lo que se queda fuera se dice con nombre y motivo, no solo el número: un
   * lead descartado en silencio es un cliente perdido que nadie busca.
   */
  const importarMeta = async (fichero) => {
    setImportando(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', fichero);
      const { data } = await axiosInstance.post('/crm/import-meta', cuerpo);
      toast.success(data.message);
      (data.descartados || []).forEach((d) => {
        toast.warning(`${d.nombre}: ${d.motivo}`, { duration: 8000 });
      });
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error));
    } finally {
      setImportando(false);
    }
  };

  const cargarConfigMeta = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/crm/meta-sync');
      setConfigMeta(data);
      setUrlMeta(data?.sheet_url || '');
    } catch (error) {
      // No poder leerla no puede impedir usar el CRM, pero sí queda dicho:
      // tragarse el error en silencio fue lo que hizo que el diálogo se
      // abriera tan normal mientras el backend contestaba otra cosa.
      console.error('No se pudo leer la configuración de Meta:', error);
      setConfigMeta(null);
    }
  }, []);

  /** Guarda el enlace y lo prueba en el acto: es la única forma de saber si
   *  la hoja es pública antes de esperar al cuarto de hora siguiente. */
  const guardarConfigMeta = async () => {
    setGuardandoMeta(true);
    try {
      const { data } = await axiosInstance.put('/crm/meta-sync',
        { sheet_url: urlMeta.trim(), activo: Boolean(urlMeta.trim()) });
      setConfigMeta(data.config);
      const nuevos = data.sincronizacion?.creados?.length ?? 0;
      toast.success(nuevos
        ? `Hoja conectada. ${nuevos} leads nuevos.`
        : 'Hoja conectada. No había leads nuevos.');
      setDialogoMeta(false);
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error));
    } finally {
      setGuardandoMeta(false);
    }
  };

  /** Mira la hoja ahora, sin esperar al cuarto de hora. */
  const sincronizarMetaAhora = async () => {
    setGuardandoMeta(true);
    try {
      const { data } = await axiosInstance.post('/crm/meta-sync/run');
      toast.success(data.message);
      (data.descartados || []).forEach((d) => {
        toast.warning(`${d.nombre}: ${d.motivo}`, { duration: 8000 });
      });
      cargarConfigMeta();
      cargar();
    } catch (error) {
      toast.error(mensajeDeError(error));
    } finally {
      setGuardandoMeta(false);
    }
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [cols, fichas, mots] = await Promise.all([
        axiosInstance.get('/crm-statuses'),
        axiosInstance.get('/leads'),
        axiosInstance.get('/crm/loss-reasons'),
      ]);
      setColumnas([...cols.data].sort((a, b) => a.order - b.order));
      setLeads(fichas.data);
      setMotivos(mots.data);
    } catch (e) {
      // Un fallo aqui dejaba la pantalla en blanco sin decir nada, porque el
      // render era {algo && (...)} y el estado se quedaba a null. Ahora se ve.
      setError(e.response?.data?.detail || e.message || 'No se pudo cargar el CRM');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cargarConfigMeta(); }, [cargarConfigMeta]);

  const columnaDe = (key) => columnas.find((c) => c.key === key);
  const faseDe = (key) => columnaDe(key)?.stage;

  // ---- Acciones ----

  const crearLead = async (datos) => {
    try {
      await axiosInstance.post('/customers', {
        ...datos,
        email: datos.email || null,
        phone: datos.phone || null,
        company: datos.company || null,
        notes: datos.notes || null,
        type: 'lead',
        status: columnas.find((c) => c.stage === 'new')?.key || 'new',
      });
      toast.success('Lead creado');
      setDialogoNuevo(false);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el lead');
    }
  };

  const heLlamado = async (lead) => {
    setLlamando(lead.id);
    try {
      const { data } = await axiosInstance.post(`/leads/${lead.id}/log-contact`, {
        tipo: 'llamada',
      });
      if (data.duplicado) {
        toast.info('Esa llamada ya estaba registrada');
      } else {
        toast.success(`${textoIntentos(data.contact_attempts)} registrado`);
      }
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo registrar la llamada');
    } finally {
      setLlamando(null);
    }
  };

  const crearPresupuesto = (lead) => {
    // Un clic desde la tarjeta, sin salir a buscar el cliente en otra pantalla.
    navigate(`/quotes?customer=${lead.id}`);
  };

  const confirmarPerdida = async (lead, motivo, detalle) => {
    try {
      await axiosInstance.post(`/leads/${lead.id}/lost`, { motivo, detalle: detalle || null });
      toast.success('Marcado como perdido');
      setFichaPerdida(null);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo marcar como perdido');
    }
  };

  const confirmarCobro = async (lead, datos) => {
    try {
      const { data } = await axiosInstance.post(`/leads/${lead.id}/mark-paid`, datos);
      if (data.ya_estaba) {
        toast.info('Ese cobro ya estaba registrado');
      } else {
        toast.success('Cobro confirmado. El presupuesto pasa a aceptado.');
        if (data.crear_ticket_contrato) await crearTicketContrato(lead);
      }
      setFichaCobro(null);
      cargar();

      // Todavia NO es cliente: pagar no da el NIF ni la direccion. Se abre
      // directamente el formulario porque este es el mejor momento para
      // pedirselos, con el cliente contento y al telefono.
      if (!data.billing_ready) setFichaDatos(lead);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo confirmar el cobro');
    }
  };

  /**
   * Abre el ticket de recogida de datos fiscales.
   *
   * Ya existia y ya funcionaba: lo unico que cambia es CUANDO salta. Antes se
   * disparaba al arrastrar la ficha a cierta columna; ahora al confirmar el
   * cobro, que es el momento en que de verdad hay que pedirle los papeles.
   *
   * Si falla, el cobro NO se pierde: ya esta registrado en el servidor.
   */
  const crearTicketContrato = async (lead) => {
    try {
      let cliente = null;
      try {
        cliente = (await axiosInstance.get(`/customers/${lead.id}`)).data;
      } catch { /* la ficha puede no tener aun datos de cliente */ }

      const v = (x) => x || 'Pendiente';
      await axiosInstance.post('/tickets', {
        subject: `Contrato de nuevo cliente: ${lead.name}`,
        description: [
          'NUEVO CLIENTE - HA PAGADO, FALTAN DATOS PARA FACTURAR',
          '',
          `Nombre/Empresa: ${v(cliente?.name || lead.name)}`,
          `Nombre comercial: ${v(cliente?.commercial_name || lead.company)}`,
          `NIF/CIF: ${v(cliente?.tax_id)}`,
          `Email: ${v(cliente?.email || lead.email)}`,
          `Teléfono: ${v(cliente?.phone || lead.phone)}`,
          `Dirección: ${v(cliente?.address)}`,
          `Ciudad: ${v(cliente?.city)}`,
          `Código postal: ${v(cliente?.postal_code)}`,
          `Provincia: ${v(cliente?.province)}`,
          '',
          `Notas: ${v(cliente?.notes || lead.notes)}`,
          '',
          'Origen: CRM - cobro confirmado',
        ].join('\n'),
        priority: 'high',
        category: 'soporte',
        customer_id: lead.id,
      });
      toast.success('Ticket de contrato creado');
    } catch (e) {
      // El cobro ya esta guardado. Esto es un aviso, no un fallo del cobro.
      console.error('Error creando el ticket de contrato:', e);
      toast.warning('Cobro registrado, pero el ticket de contrato falló. Créalo a mano.');
    }
  };

  // ---- Arrastre ----

  const alSoltar = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    const lead = leads.find((l) => l.id === draggableId);
    const destino = destination.droppableId;
    const fase = faseDe(destino);

    // Perdido y Ganado NO se alcanzan arrastrando: hacen falta el motivo y el
    // cobro. El servidor los rechaza a proposito, asi que aqui se intercepta y
    // se abre el dialogo correspondiente.
    //
    // Esta comprobacion va ANTES de la actualizacion optimista: si movieramos
    // la tarjeta primero, cancelar el dialogo la dejaria en una columna donde
    // el servidor no la tiene.
    if (fase === 'lost') { setFichaPerdida(lead); return; }
    if (fase === 'won') { setFichaCobro(lead); return; }

    setLeads((previas) =>
      previas.map((l) => (l.id === draggableId ? { ...l, status: destino } : l)));

    try {
      await axiosInstance.put(`/leads/${draggableId}`, { status: destino });
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo mover la ficha');
      cargar();  // revertir
    }
  };

  // ---- Render ----

  const porColumna = columnas.reduce((acc, c) => {
    acc[c.key] = leads.filter((l) => l.status === c.key);
    return acc;
  }, {});

  const enRojo = leads.filter(
    (l) => nivelDeAlerta(l.days_stalled, columnaDe(l.status)?.warn_days,
                         columnaDe(l.status)?.alert_days) === 'alerta'
  ).length;

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="crm-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="crm-title">CRM</h1>
            <p className="text-slate-600">
              {enRojo > 0
                ? <span className="text-red-600 font-medium" data-testid="aviso-parados">
                    {enRojo} {enRojo === 1 ? 'ficha lleva' : 'fichas llevan'} demasiado tiempo sin tocar
                  </span>
                : 'Todo al día'}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Los leads de las campañas de Meta, mientras la agencia conecta
                el webhook. Se sube el CSV tal cual lo exporta Meta; reimportar
                la misma hoja no duplica nada. */}
            <input
              type="file" accept=".csv,text/csv" ref={ficheroMeta}
              className="hidden"
              onChange={(e) => {
                const fichero = e.target.files?.[0];
                e.target.value = '';
                if (fichero) importarMeta(fichero);
              }}
              data-testid="input-csv-meta"
            />
            <Button variant="outline" onClick={() => setDialogoMeta(true)}
                    className={configMeta?.last_error
                      ? 'border-amber-500 text-amber-700 hover:bg-amber-50'
                      : 'border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white'}
                    data-testid="meta-btn">
              {configMeta?.last_error
                ? <AlertTriangle className="w-4 h-4 mr-2" />
                : <RefreshCw className="w-4 h-4 mr-2" />}
              Leads de Meta
            </Button>
            <Button onClick={() => setDialogoNuevo(true)}
                    className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                    data-testid="create-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo lead
            </Button>
          </div>
        </div>

        {cargando && (
          <div className="text-slate-500 py-12 text-center" data-testid="crm-cargando">
            Cargando el tablero...
          </div>
        )}

        {error && !cargando && (
          <div className="border border-red-200 bg-red-50 rounded-lg p-4" data-testid="crm-error">
            <p className="text-red-800 font-medium">No se pudo cargar el CRM</p>
            <p className="text-red-700 text-sm mt-1">{String(error)}</p>
            <Button variant="outline" className="mt-3" onClick={cargar}>Reintentar</Button>
          </div>
        )}

        {!cargando && !error && (
          <DragDropContext onDragEnd={alSoltar}>
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="kanban-board">
              {columnas.map((columna) => (
                <div key={columna.key}
                     className="bg-slate-50 rounded-lg border border-slate-200 flex-shrink-0"
                     style={{ width: '230px' }}
                     data-testid={`columna-${columna.key}`}>
                  <div className="p-2 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <Badge className={COLORES_BADGE[columna.color] || COLORES_BADGE.gray}
                             title={columna.description}>
                        {columna.name}
                      </Badge>
                      <span className="text-xs font-medium text-slate-500 bg-white px-1.5 py-0.5 rounded">
                        {porColumna[columna.key]?.length || 0}
                      </span>
                    </div>
                  </div>

                  <Droppable droppableId={columna.key}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="p-1.5 space-y-1.5 min-h-[220px] max-h-[560px] overflow-y-auto"
                        style={{ backgroundColor: snapshot.isDraggingOver ? '#dbeafe' : 'transparent' }}
                      >
                        {(porColumna[columna.key] || []).map((lead, index) => (
                          <Draggable key={lead.id} draggableId={lead.id} index={index}>
                            {(prov) => (
                              <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}>
                                <TarjetaLead
                                  lead={lead}
                                  columna={columna}
                                  ocupado={llamando === lead.id}
                                  onLlamar={heLlamado}
                                  onPresupuesto={crearPresupuesto}
                                  onDatos={setFichaDatos}
                                  onAbrir={(id) => navigate(`/clients/${id}`)}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        )}

        <DialogoNuevoLead open={dialogoNuevo} onOpenChange={setDialogoNuevo} onCreado={crearLead} />
        <DialogoPerdido
          open={!!fichaPerdida}
          onOpenChange={(v) => !v && setFichaPerdida(null)}
          lead={fichaPerdida}
          motivos={motivos}
          onConfirmar={confirmarPerdida}
        />
        <DialogoCobro
          open={!!fichaCobro}
          onOpenChange={(v) => !v && setFichaCobro(null)}
          lead={fichaCobro}
          onConfirmar={confirmarCobro}
        />
        <DialogoDatosFacturacion
          open={!!fichaDatos}
          onOpenChange={(v) => !v && setFichaDatos(null)}
          customerId={fichaDatos?.id}
          nombre={fichaDatos?.name}
          onGuardado={cargar}
        />
        {/* La hoja de las campañas de Meta. Angel, 22-sep-2026: "¿por qué
            me haces descargar el excel y no lo revisas tú cada cierto tiempo?".
            Se pega el enlace una vez y se mira sola cada cuarto de hora. */}
        <Dialog open={dialogoMeta} onOpenChange={setDialogoMeta}>
          <DialogContent className="max-w-lg" data-testid="dialogo-meta">
            <DialogHeader>
              <DialogTitle>Leads de las campañas de Meta</DialogTitle>
              <DialogDescription>
                Se miran solos cada cuarto de hora y entran en la primera columna
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5">
              <Label htmlFor="url-meta">Enlace de la hoja</Label>
              <Input
                id="url-meta" type="url" value={urlMeta}
                onChange={(e) => setUrlMeta(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                data-testid="url-meta"
              />
              <p className="text-xs text-slate-500">
                El que te da Google al pulsar Compartir. La hoja tiene que estar
                como <strong>&quot;Cualquiera con el enlace&quot;</strong> en modo Lector.
              </p>
            </div>

            {/* Cómo fue la última pasada. Una sincronización que lleva tres
                días fallando en silencio es peor que no tenerla. */}
            {configMeta?.last_error && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> La última vez falló
                </p>
                <p className="mt-1">{configMeta.last_error}</p>
              </div>
            )}
            {configMeta?.last_sync && !configMeta?.last_error && (
              <p className="text-sm text-slate-600 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Última revisión: {new Date(configMeta.last_sync).toLocaleString('es-ES')}
                {configMeta.last_result
                  ? ` · ${configMeta.last_result.creados} nuevos` : ''}
              </p>
            )}

            <div className="border-t pt-3">
              <p className="text-xs text-slate-500 mb-2">
                ¿Tienes un archivo suelto? Súbelo aquí. Acepta .xlsx y .csv.
              </p>
              <Button variant="outline" size="sm"
                      onClick={() => ficheroMeta.current?.click()}
                      disabled={importando}
                      data-testid="importar-meta-btn">
                <Upload className="w-4 h-4 mr-2" />
                {importando ? 'Importando...' : 'Subir un archivo'}
              </Button>
            </div>

            <DialogFooter className="gap-2">
              {configMeta?.activo && (
                <Button variant="outline" onClick={sincronizarMetaAhora}
                        disabled={guardandoMeta}
                        data-testid="sincronizar-meta-ahora">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Mirar ahora
                </Button>
              )}
              <Button onClick={guardarConfigMeta} disabled={guardandoMeta}
                      className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                      data-testid="guardar-meta">
                {guardandoMeta ? 'Probando...' : 'Guardar y conectar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
